'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'

import {
  AccessDeniedModal,
} from '@/components/access/AccessDeniedModal'

import type {
  AccessResult,
} from '@/components/access/types'

type GateOptions = {
  title?: string
}

type AIAGateCtx = {
  guard: (
    fn: () => void | Promise<void>,
    opts?: GateOptions
  ) => Promise<boolean>

  check: (
    opts?: GateOptions
  ) => Promise<boolean>

  openPaywall: (
    opts?: GateOptions
  ) => void

  closePaywall: () => void

  access:
    AccessResult | null
}

const Ctx =
  createContext<AIAGateCtx | null>(
    null
  )

export function AIAGateProvider({
  sellerId,
  children,
}: {
  sellerId?:
    number | null

  children:
    React.ReactNode
}) {
  const [
    access,
    setAccess,
  ] =
    useState<AccessResult | null>(
      null
    )

  const [
    open,
    setOpen,
  ] =
    useState(false)

  const [
    title,
    setTitle,
  ] =
    useState(
      'Activa una nueva AIA'
    )

  const check =
    useCallback(
      async (
        opts?: GateOptions
      ) => {
        if (
          opts?.title
        ) {
          setTitle(
            opts.title
          )
        }

        /*
         * AIA NO tiene bypass
         * para owner.
         */
        if (!sellerId) {
          const denied = {
            allowed:
              false,

            reason:
              'product',

            productIds:
              [],
          } as AccessResult

          setAccess(
            denied
          )

          return false
        }

        try {
          const res =
            await fetch(
              `/api/access/aia?seller_id=${sellerId}`,
              {
                cache:
                  'no-store',

                credentials:
                  'include',
              }
            )

          const data =
            (
              await res.json()
            ) as AccessResult

          setAccess(
            data
          )

          return Boolean(
            data.allowed
          )
        } catch (
          error
        ) {
          console.error(
            '[AIA GATE CHECK ERROR]',
            error
          )

          const denied = {
            allowed:
              false,

            reason:
              'product',

            productIds:
              [],
          } as AccessResult

          setAccess(
            denied
          )

          return false
        }
      },
      [
        sellerId,
      ]
    )

  const guard =
    useCallback(
      async (
        fn:
          () =>
            void |
            Promise<void>,

        opts?:
          GateOptions
      ) => {
        const ok =
          await check(
            opts
          )

        if (!ok) {
          return false
        }

        await fn()

        return true
      },
      [
        check,
      ]
    )

  const openPaywall =
    useCallback(
      (
        opts?: GateOptions
      ) => {
        if (
          opts?.title
        ) {
          setTitle(
            opts.title
          )
        }

        if (
          !access ||
          access.allowed
        ) {
          return
        }

        setOpen(
          true
        )
      },
      [
        access,
      ]
    )

  const closePaywall =
    useCallback(
      () => {
        setOpen(
          false
        )
      },
      []
    )

  const value =
    useMemo(
      () => ({
        guard,
        check,
        openPaywall,
        closePaywall,
        access,
      }),
      [
        guard,
        check,
        openPaywall,
        closePaywall,
        access,
      ]
    )

  return (
    <Ctx.Provider
      value={
        value
      }
    >
      {children}

      {access &&
        !access.allowed && (
          <Dialog
            open={
              open
            }
            onOpenChange={
              setOpen
            }
          >
            <DialogContent
              className="
                max-w-md
              "
            >
              <AccessDeniedModal
                title={
                  title
                }

                access={
                  access
                }

                type="tool"

                mode="restricted"

                step="preview"
              />
            </DialogContent>
          </Dialog>
        )}
    </Ctx.Provider>
  )
}

export function useAIAGate() {
  const value =
    useContext(
      Ctx
    )

  if (!value) {
    throw new Error(
      'useAIAGate must be used within <AIAGateProvider />'
    )
  }

  return value
}